import { Component, ElementRef, OnInit, ViewChild } from '@angular/core';
import { BaseComponent } from '../base/base.component';
import { User, getEffectiveSubscriptionStatus, SubscriptionStatus } from '../../models/RestModels';
import { ItemInfo, SubscriptionInfo } from 'src/app/models/models';
import { SearchObject } from '../../services/Rest';

interface SubscriptionSummaryRow {
	item_id: number | null;
	item_name: string;
	user_count: number;
	period_count: number;
	active_count: number;
}

@Component
({
	selector: 'app-list-subscription',
	templateUrl: './list-subscription.component.html',
	styleUrls: ['./list-subscription.component.css']
})
export class ListSubscriptionComponent extends BaseComponent implements OnInit
{
	mode: 'summary' | 'client' | 'room' = 'summary';

	subscription_item_list: ItemInfo[] = [];
	summary: SubscriptionSummaryRow[] = [];
	total_users: number = 0;
	total_periods: number = 0;

	client_search_str: string = '';
	client_list: User[] = [];
	selected_client: User | null = null;
	client_subscriptions: SubscriptionInfo[] = [];

	asset_item_list: ItemInfo[] = [];
	selected_asset_item_id: number | null = null;
	serial_list: { id: number; serial_number: string }[] = [];
	selected_serial_id: number | null = null;
	serial_history: SubscriptionInfo[] = [];

	@ViewChild('client_detail_card') client_detail_card: ElementRef;
	@ViewChild('room_detail_card') room_detail_card: ElementRef;

	scrollTo(ref: ElementRef | undefined): void {
		setTimeout(() => {
			if (ref?.nativeElement) {
				ref.nativeElement.scrollIntoView({ block: 'start', behavior: 'smooth' });
			}
		}, 100);
	}

	article_subscriptions: SubscriptionInfo[] = [];
	show_client_detail: boolean = false;
	show_room_detail: boolean = false;

	// Listas crudas del servidor y reloj de vigencia: la vigencia se recalcula
	// sobre lo ya cargado para que una suscripcion que vence mientras la pantalla
	// esta abierta cambie sola de estado (y de filtro) sin pedir datos otra vez.
	raw_summary_list: SubscriptionInfo[] = [];
	raw_article_list: SubscriptionInfo[] = [];
	validity_timer: any = null;

	ngOnInit(): void {
		this.loadItems();
		this.loadSummary();
		this.validity_timer = setInterval(() => this.refreshValidity(), 30000);
	}

	ngOnDestroy(): void {
		if (this.validity_timer) {
			clearInterval(this.validity_timer);
			this.validity_timer = null;
		}

		super.ngOnDestroy();
	}

	// Reevalua periodo contra la hora actual sobre los datos cargados.
	refreshValidity(): void {
		if (this.raw_summary_list.length) {
			this.buildSummary(this.filterByValidity(this.raw_summary_list));
		}

		if (this.raw_article_list.length) {
			this.article_subscriptions = this.filterByValidity(this.raw_article_list);
		}
	}

	loadItems(): void {
		this.sink = this.rest.item_info.search({
			eq: { is_subscription: 1, status: 'ACTIVE' } as any,
			limit: 200
		}).subscribe({
			next: (response) => {
				this.subscription_item_list = response.data || [];
			},
			error: () => { this.subscription_item_list = []; }
		});

		this.sink = this.rest.item_info.search({
			eq: { has_serial_number: 'YES', status: 'ACTIVE' } as any,
			limit: 200
		}).subscribe({
			next: (response) => {
				this.asset_item_list = response.data || [];
			},
			error: () => { this.asset_item_list = []; }
		});
	}

	summary_status: string = 'ACTIVE';

	// El filtro por vigencia (Activas / Expiradas) se resuelve por periodo, no por
	// el status guardado: ver getEffectiveSubscriptionStatus. Solo se acota en el
	// servidor cuando el estado no depende del tiempo, porque el buscador no tiene
	// IN y una fila EXPIRED puede estar guardada como ACTIVE (periodo ya vencido).
	loadSummary(): void {
		this.is_loading = true;
		const search: any = { limit: 1000, sort_order: ['starts_at_DESC'] };
		search.eq = {} as any;

		if (this.summary_status && this.summary_status != 'EXPIRED') {
			search.eq.status = this.summary_status;
		}

		this.sink = this.rest.subscription_info.search(search).subscribe({
			next: (response) => {
				this.is_loading = false;
				this.raw_summary_list = response.data || [];
				this.buildSummary(this.filterByValidity(this.raw_summary_list));
			},
			error: (error) => this.showError(error)
		});
	}

	buildSummary(list: SubscriptionInfo[]): void {
		const by_item = new Map<number | null, SubscriptionInfo[]>();

		for (const info of list) {
			const key = info.subscription.item_id;
			if (!by_item.has(key)) {
				by_item.set(key, []);
			}
			by_item.get(key).push(info);
		}

		this.summary = [];
		const all_users = new Set<number>();
		let periods = 0;

		by_item.forEach((rows, item_id) => {
			const users = new Set<number>();
			let active = 0;

			for (const row of rows) {
				users.add(row.subscription.user_id);
				all_users.add(row.subscription.user_id);
				if (this.getSubscriptionStatus(row) == 'ACTIVE') {
					active++;
				}
			}

			periods += rows.length;
			this.summary.push({
				item_id: item_id,
				item_name: rows[0]?.item?.name || ('Artículo #' + item_id),
				user_count: users.size,
				period_count: rows.length,
				active_count: active
			});
		});

		this.summary.sort((a, b) => b.active_count - a.active_count || b.period_count - a.period_count);
		this.total_users = all_users.size;
		this.total_periods = periods;
	}

	expanded_item_id: number | null = null;

	toggleArticle(row: SubscriptionSummaryRow): void {
		if (this.expanded_item_id === row.item_id) {
			this.expanded_item_id = null;
			this.article_subscriptions = [];
			this.raw_article_list = [];
			return;
		}

		this.expanded_item_id = row.item_id;
		this.article_subscriptions = [];
		this.raw_article_list = [];
		this.is_loading = true;
		const article_eq: any = { item_id: row.item_id };

		if (this.summary_status && this.summary_status != 'EXPIRED') {
			article_eq.status = this.summary_status;
		}

		this.sink = this.rest.subscription_info.search({
			eq: article_eq,
			limit: 500,
			sort_order: ['starts_at_DESC']
		}).subscribe({
			next: (response) => {
				this.is_loading = false;
				this.raw_article_list = response.data || [];
				this.article_subscriptions = this.filterByValidity(this.raw_article_list);
			},
			error: (error) => this.showError(error)
		});
	}

	searchClients(): void {
		if (!this.client_search_str || this.client_search_str.length < 2) {
			this.client_list = [];
			return;
		}

		const search: SearchObject<User> = this.getEmptySearch();
		search.limit = 20;
		search.eq.type = 'CLIENT';
		search.lk = { name: this.client_search_str } as any;

		this.sink = this.rest.user.search(search).subscribe({
			next: (response) => {
				this.client_list = response.data || [];
			},
			error: (error) => this.showError(error)
		});
	}

	selectClient(client: User): void {
		this.selected_client = client;
		this.client_list = [];
		this.client_subscriptions = [];
		this.is_loading = true;
		this.sink = this.rest.subscription_info.search({
			eq: { user_id: client.id } as any,
			limit: 200,
			sort_order: ['expires_at_DESC']
		}).subscribe({
			next: (response) => {
				this.is_loading = false;
				this.client_subscriptions = response.data || [];
				this.show_client_detail = true;
			},
			error: (error) => this.showError(error)
		});
	}

	loadSerials(): void {
		this.serial_list = [];
		this.selected_serial_id = null;
		this.serial_history = [];

		if (!this.selected_asset_item_id) {
			return;
		}

		this.is_loading = true;
		this.sink = this.rest.serial_info.search({
			eq: { item_id: this.selected_asset_item_id, status: 'ACTIVE' } as any,
			limit: 500
		}).subscribe({
			next: (response) => {
				this.is_loading = false;
				this.serial_list = (response.data || []).map((row: any) => ({
					id: row.serial?.id ?? row.id,
					serial_number: row.serial?.serial_number ?? row.serial_number
				}));
			},
			error: (error) => this.showError(error)
		});
	}

	loadSerialHistory(): void {
		this.serial_history = [];

		if (!this.selected_serial_id) {
			return;
		}

		this.is_loading = true;
		this.sink = this.rest.subscription_info.search({
			eq: { serial_id: this.selected_serial_id } as any,
			limit: 500,
			sort_order: ['starts_at_DESC']
		}).subscribe({
			next: (response) => {
				this.is_loading = false;
				this.serial_history = response.data || [];
				this.show_room_detail = true;
			},
			error: (error) => this.showError(error)
		});
	}

	getItemName(info: SubscriptionInfo): string {
		return info.item?.name || ('Artículo #' + info.subscription.item_id);
	}

	getCustomerName(info: SubscriptionInfo): string {
		return info.customer?.name || ('Usuario #' + info.subscription.user_id);
	}

	// Estado para mostrar: la vigencia se calcula por periodo, no se lee del
	// status guardado (getEffectiveSubscriptionStatus).
	getSubscriptionStatus(info: SubscriptionInfo): SubscriptionStatus {
		return getEffectiveSubscriptionStatus(info.subscription);
	}

	// Aplica el filtro elegido sobre la vigencia calculada.
	filterByValidity(list: SubscriptionInfo[]): SubscriptionInfo[] {
		if (!this.summary_status) {
			return list;
		}

		return list.filter((info) => this.getSubscriptionStatus(info) == this.summary_status);
	}
}
